import { Component, Input, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import * as Prism from 'prismjs';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';

// "Try It Yourself" widget embedded in the dark-mode-vs-light-mode post. One slider
// sets the ambient room light; two real Prism-highlighted code boxes, painted with
// the actual Codigrate palettes (Aurora Borealis for dark, Everest for light), sit
// in that room while the widget acts out the post's physics: the dark pane turns
// into a mirror next to a sunny window and the light pane becomes the room's
// brightest, glowing object at night. A verdict line restates the post's rule for
// the current light. The post markdown marks a mount
// point with <!--MODE-START-->...<!--MODE-END--> around a static fallback (kept for
// crawlers and no-JS readers); BlogPostPageComponent swaps this live component in.
@Component( {
  selector: 'app-mode-room',
  templateUrl: './mode-room.component.html',
  styleUrls: [ './mode-room.component.scss' ],
} )
export class ModeRoomComponent implements OnInit {

  // Heading shown OUTSIDE the glass panel (matches the light-mixer pattern).
  @Input() heading = 'Try It Yourself';

  // Ambient room light, 0 (midnight desk) .. 100 (sunny window). Opens in a dim
  // room, the setting where the two panes differ the most.
  ambient = 22;

  // The same short, real snippet highlighted once by Prism and painted by each
  // pane's own theme palette (see the token colors in the SCSS).
  code: SafeHtml = '';

  private readonly source = [
    '// pick a theme by the light',
    'const room = readRoomLight();',
    'const dim = room < 50;',
    'const theme = dim ? \'aurora\' : \'everest\';',
    'applyTheme( theme );',
  ].join( '\n' );

  // One engagement event on first interaction, not per slider tick.
  private tracked = false;

  constructor( private sanitizer: DomSanitizer, private analyticsService: AnalyticsService ) {
  }

  ngOnInit(): void {
    this.code = this.sanitizer.bypassSecurityTrustHtml(
      Prism.highlight( this.source, Prism.languages[ 'javascript' ], 'javascript' ) );
  }

  onSlide( value: string ): void {
    this.ambient = Math.max( 0, Math.min( 100, +value || 0 ) );
    if ( !this.tracked ) {
      this.tracked = true;
      this.analyticsService.logEvent( 'Blog Widget Used', { widget: 'mode-room' } );
    }
  }

  // The room behind the panes: a midnight blue-grey that warms and brightens toward
  // late-afternoon window light.
  get sceneBg(): string {
    const a = this.ambient / 100;
    const mix = ( from: number, to: number ) => Math.round( from + ( to - from ) * a );
    return `rgb(${ mix( 13, 240 ) },${ mix( 17, 236 ) },${ mix( 27, 227 ) })`;
  }

  // Which little glyph sits next to the room label (see the ngSwitch in the template).
  get roomIcon(): string {
    if ( this.ambient < 15 ) {
      return 'moon';
    }
    if ( this.ambient < 40 ) {
      return 'lamp';
    }
    if ( this.ambient < 70 ) {
      return 'bulb';
    }
    return 'sun';
  }

  // The room label under the slider.
  get roomLabel(): string {
    if ( this.ambient < 15 ) {
      return 'Midnight Desk';
    }
    if ( this.ambient < 40 ) {
      return 'Desk Lamp';
    }
    if ( this.ambient < 70 ) {
      return 'Office Light';
    }
    return 'Sunny Window';
  }

  // Mirror effect: near a bright window the dark pane mostly reflects the room.
  get glare(): number {
    return this.clamp01( ( this.ambient - 55 ) / 45 ) * 0.5;
  }

  // In a dark room the light pane is the brightest object in sight and blooms.
  get glow(): number {
    return this.clamp01( ( 40 - this.ambient ) / 40 ) * 0.85;
  }

  // The bloom as light spilling off the pane into the dark room: the counterpart
  // of the dark pane's window glare, bound as the light pane's box-shadow. Tinted
  // with Everest's own editorBackground, the colour the pane actually emits.
  get glowShadow(): string {
    const a = this.glow;
    const layer = ( blur: number, spread: number, alpha: number ) =>
      `0 0 ${ blur }px ${ spread }px rgba(253,254,255,${ alpha.toFixed( 3 ) })`;
    return `${ layer( 24, 5, a * 0.9 ) }, ${ layer( 58, 14, a * 0.5 ) }`;
  }

  // Short, changing status line under each pane, naming what the reader can see.
  get darkStatus(): string {
    if ( this.ambient < 35 ) {
      return 'Resting comfortably in the dim room';
    }
    if ( this.ambient < 65 ) {
      return 'Still readable, losing its advantage';
    }
    return 'Reflecting the window like dark glass';
  }

  get lightStatus(): string {
    if ( this.ambient < 35 ) {
      return 'The brightest object in the room';
    }
    if ( this.ambient < 65 ) {
      return 'Comfortable in the balanced light';
    }
    return 'Sharp, the page matches the room';
  }

  // The decision for the current light, shown with the site's Appearance glyphs
  // (the same sun / yin-yang / moon icons the theme toggle uses).
  get decision(): string {
    if ( this.ambient < 35 ) {
      return 'dark';
    }
    if ( this.ambient < 65 ) {
      return 'both';
    }
    return 'light';
  }

  get decisionLabel(): string {
    if ( this.ambient < 35 ) {
      return 'Dark';
    }
    if ( this.ambient < 65 ) {
      return 'Both';
    }
    return 'Light';
  }

  // The reasoning after the decision, one short clause per light band.
  get decisionReason(): string {
    if ( this.ambient < 35 ) {
      return 'in a dim room the dark theme is the calmer read.';
    }
    if ( this.ambient < 65 ) {
      return 'in between, both modes work fine.';
    }
    return 'next to a bright window the light page reads sharpest.';
  }


  private clamp01( value: number ): number {
    return Math.max( 0, Math.min( 1, value ) );
  }
}
